import { ReactNode, forwardRef } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../button/button';
import { Icon } from '../icon/icon';
import { ProgressRingIcon } from '../icon/progressRingIcon';

interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

interface TabsListProps {
  children: ReactNode;
  className?: string;
}

interface TabsTabProps {
  children: ReactNode;
  isSelected?: boolean;
  onSelect?: () => void;
  onClose?: () => void;
  className?: string;
  icon?: ReactNode;
  hasUnsavedChanges?: boolean;
  'data-testid'?: string;
  progress?: number;
  error?: string;
}

interface TabsPanelProps {
  children: ReactNode;
  isSelected?: boolean;
  className?: string;
}

interface TabsAddProps extends React.ComponentPropsWithoutRef<typeof Button> {
  onClick?: () => void;
  className?: string;
}

const TabsRoot = forwardRef<HTMLDivElement, TabsProps>(
  ({ children, className }, ref) => {
    return (
      <div ref={ref} className={cn('flex w-full flex-col', className)}>
        {children}
      </div>
    );
  },
);
TabsRoot.displayName = 'Tabs.Root';

const TabsList = forwardRef<HTMLDivElement, TabsListProps>(
  ({ children, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center border-b border-gray-200 dark:border-gray-800',
          className,
        )}
      >
        {children}
      </div>
    );
  },
);
TabsList.displayName = 'Tabs.List';
const TabsAdd = forwardRef<HTMLButtonElement, TabsAddProps>(
  ({ onClick, className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        onClick={onClick}
        variant="ghost"
        leftIcon="lucide:plus"
        iconOnly
        className={cn(className)}
        {...props}
      />
    );
  },
);
TabsAdd.displayName = 'Tabs.Add';

const TabsTab = forwardRef<HTMLDivElement, TabsTabProps>(
  (
    {
      children,
      isSelected,
      onSelect,
      onClose,
      className,
      icon,
      hasUnsavedChanges,
      'data-testid': dataTestId,
      progress,
      error,
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        onClick={onSelect}
        className={cn(
          'flex min-w-[200px] max-w-[300px] cursor-pointer items-center gap-2 border border-gray-200 px-4 py-0 dark:border-gray-800',
          '-mb-[2px] text-sm text-grey-800 dark:text-grey-100',
          'focus:outline-none',
          isSelected
            ? 'border-b-0 bg-white dark:bg-[#11171c]'
            : 'bg-gray-100 dark:bg-gray-800',
          'group',
          className,
        )}
        data-testid={dataTestId}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span className="min-w-0 flex-1 truncate text-left group-hover:text-blue-600 dark:group-hover:text-blue-500">
          {children}
        </span>
        {!error && progress === 100 && (
          <Icon icon="lucide:check-circle" className="h-4 w-4 text-green-500" />
        )}
        {error && (
          <Icon icon="lucide:circle-x" className="h-4 w-4 text-red-500" />
        )}
        {!!progress && progress < 100 && (
          <ProgressRingIcon
            progress={progress}
            className="h-4 w-4 text-green-500"
          />
        )}
        {onClose && (
          <div className="relative flex-shrink-0">
            {hasUnsavedChanges && (
              <div className="absolute inset-0 flex items-center justify-center group-hover:hidden">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
              </div>
            )}
            <Button
              variant="ghost"
              iconOnly
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className={cn(
                'h-auto px-0',
                hasUnsavedChanges ? 'invisible group-hover:visible' : 'visible',
              )}
              leftIcon="lucide:x"
            />
          </div>
        )}
      </div>
    );
  },
);
TabsTab.displayName = 'Tabs.Tab';

const TabsPanel = forwardRef<HTMLDivElement, TabsPanelProps>(
  ({ children, className }, ref) => {
    return (
      <div ref={ref} className={cn('p-4', className)}>
        {children}
      </div>
    );
  },
);
TabsPanel.displayName = 'Tabs.Panel';

export const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Add: TabsAdd,
  Tab: TabsTab,
  Panel: TabsPanel,
};
